import { useEffect } from 'react';

import {
  isPrerendering,
  markPrerenderReady,
  resetPrerenderReady
} from '../services/prerender';

const jsonLdContainsType = type => Array.from(
  document.querySelectorAll('script[type="application/ld+json"]')
).some(script => {
  try {
    const value = JSON.parse(script.textContent);
    const entries = value && Array.isArray(value['@graph']) ? value['@graph'] : [value];
    return entries.some(entry => entry && entry['@type'] === type);
  } catch (err) {
    return false;
  }
});

function usePrerenderReady(ready, pageType, options = {}) {
  const {
    selectors = [],
    absentSelectors = [],
    canonical,
    jsonLdType,
    validate
  } = options;
  const selectorKey = selectors.join('|');
  const absentSelectorKey = absentSelectors.join('|');

  useEffect(() => {
    if (!isPrerendering()) return undefined;

    resetPrerenderReady();
    if (!ready) return undefined;

    const frame = window.requestAnimationFrame(() => {
      const requiredSelectors = selectorKey ? selectorKey.split('|') : [];
      const excludedSelectors = absentSelectorKey ? absentSelectorKey.split('|') : [];
      const requiredElementsExist = requiredSelectors.every(selector => document.querySelector(selector));
      const excludedElementsAreAbsent = excludedSelectors.every(selector => !document.querySelector(selector));
      const canonicalExists = !canonical || Array.from(
        document.querySelectorAll('link[rel="canonical"]')
      ).some(link => link.getAttribute('href') === canonical);
      const structuredDataExists = !jsonLdType || jsonLdContainsType(jsonLdType);
      const customValidationPassed = !validate || validate();

      if (
        requiredElementsExist &&
        excludedElementsAreAbsent &&
        canonicalExists &&
        structuredDataExists &&
        customValidationPassed
      ) {
        markPrerenderReady(pageType);
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [
    ready,
    pageType,
    canonical,
    jsonLdType,
    selectorKey,
    absentSelectorKey,
    validate
  ]);
}

export default usePrerenderReady;
